import type { ChartTheme } from "./dataAnalysisUtils";

type PlotlyTrace = Record<string, unknown>;

type PlotlyFigure = {
  data: PlotlyTrace[];
  layout: Record<string, unknown>;
};

type ChartPalette = {
  bg: string;
  bar: string;
  edge: string;
  text: string;
  gridColor: string;
  accent: string;
  danger: string;
};

function getPalette(theme: ChartTheme): ChartPalette {
  if (theme === "dark") {
    return {
      bg: "#1a1a2e",
      bar: "#6366f1",
      edge: "#818cf8",
      text: "#9ca3af",
      gridColor: "#374151",
      accent: "#f59e0b",
      danger: "#ef4444",
    };
  }
  return {
    bg: "#ffffff",
    bar: "#4f46e5",
    edge: "#6366f1",
    text: "#374151",
    gridColor: "#d1d5db",
    accent: "#d97706",
    danger: "#dc2626",
  };
}

function makeAxis(p: ChartPalette, title?: string) {
  return {
    gridcolor: p.gridColor,
    zerolinecolor: p.gridColor,
    ...(title ? { title: { text: title } } : {}),
  };
}

function makeLayout(p: ChartPalette, xTitle?: string, yTitle?: string): Record<string, unknown> {
  return {
    paper_bgcolor: p.bg,
    plot_bgcolor: p.bg,
    font: { color: p.text, size: 11 },
    xaxis: makeAxis(p, xTitle),
    yaxis: makeAxis(p, yTitle),
    margin: { l: 50, r: 30, t: 40, b: 50 },
  };
}

// Pins both axes linear+autorange so Plotly can't index numeric ticks by position.
function makeLinearLayout(
  p: ChartPalette,
  xTitle?: string,
  yTitle?: string,
): Record<string, unknown> {
  const layout = makeLayout(p, xTitle, yTitle);
  (layout.xaxis as Record<string, unknown>).type = "linear";
  (layout.xaxis as Record<string, unknown>).autorange = true;
  (layout.yaxis as Record<string, unknown>).type = "linear";
  (layout.yaxis as Record<string, unknown>).autorange = true;
  return layout;
}

// Histogram (requires pre-computed bins from numpy)

export type HistogramComputeResult = {
  bins: number[];
  counts: number[];
};

function buildHistogram(computed: HistogramComputeResult, p: ChartPalette): PlotlyFigure {
  return {
    data: [
      {
        type: "bar",
        x: computed.bins,
        y: computed.counts,
        marker: { color: p.bar, line: { color: p.edge, width: 0.5 } },
      },
    ],
    layout: makeLinearLayout(p, "Value", "Frequency"),
  };
}

// FFT (requires pre-computed frequencies/magnitudes from numpy)

export type FFTComputeResult = {
  frequencies: number[];
  magnitudes: number[];
};

function buildFFT(computed: FFTComputeResult, p: ChartPalette): PlotlyFigure {
  return {
    data: [
      {
        type: "scatter",
        x: computed.frequencies,
        y: computed.magnitudes,
        mode: "lines",
        fill: "tozeroy",
        line: { color: p.bar, width: 1 },
        fillcolor: `${p.bar}4D`,
      },
    ],
    layout: makeLinearLayout(p, "Frequency", "Magnitude"),
  };
}

// Polynomial Fit (requires numpy polyfit)

export type PolyFitComputeResult = {
  x: number[];
  fitted: number[];
  original: number[];
  degree: number;
  r_squared: number;
};

function buildPolyFit(computed: PolyFitComputeResult, p: ChartPalette): PlotlyFigure {
  return {
    data: [
      {
        type: "scatter",
        y: computed.original,
        mode: "markers",
        marker: { color: p.bar, size: 5, opacity: 0.6 },
        name: "Data",
      },
      {
        type: "scatter",
        x: computed.x,
        y: computed.fitted,
        mode: "lines",
        line: { color: p.accent, width: 2 },
        name: `Fit (deg ${computed.degree}, R²=${computed.r_squared.toFixed(3)})`,
      },
    ],
    layout: {
      ...makeLinearLayout(p, "Index", "Value"),
      showlegend: true,
      legend: { font: { color: p.text, size: 10 } },
    },
  };
}

// Boxplot (Plotly handles stats natively)

function buildBoxplot(data: number[], p: ChartPalette): PlotlyFigure {
  return {
    data: [
      {
        type: "box",
        x: data,
        marker: { color: p.bar, outliercolor: p.danger },
        line: { color: p.edge },
        boxmean: true,
      },
    ],
    layout: {
      ...makeLinearLayout(p, "Value"),
      yaxis: { ...makeAxis(p), type: "linear", autorange: true, showticklabels: false },
    },
  };
}

function buildLine(data: number[], p: ChartPalette): PlotlyFigure {
  return {
    data: [
      {
        type: "scatter",
        y: data,
        mode: "lines+markers",
        line: { color: p.bar, width: 1.5 },
        marker: { color: p.bar, size: 4 },
      },
    ],
    layout: makeLinearLayout(p, "Index", "Value"),
  };
}

function buildBar(data: number[], p: ChartPalette): PlotlyFigure {
  return {
    data: [
      {
        type: "bar",
        y: data,
        marker: { color: p.bar, line: { color: p.edge, width: 0.5 } },
      },
    ],
    layout: makeLinearLayout(p, "Index", "Value"),
  };
}

function buildHeatmap(data2d: number[][], p: ChartPalette): PlotlyFigure {
  return {
    data: [
      {
        type: "heatmap",
        z: data2d,
        colorscale: "Viridis",
      },
    ],
    layout: makeLayout(p),
  };
}

function buildSurface(data2d: number[][], p: ChartPalette): PlotlyFigure {
  return {
    data: [
      {
        type: "surface",
        z: data2d,
        colorscale: "Viridis",
      },
    ],
    layout: {
      ...makeLayout(p),
      scene: {
        xaxis: { title: "Col", ...makeAxis(p) },
        yaxis: { title: "Row", ...makeAxis(p) },
        zaxis: { ...makeAxis(p) },
        bgcolor: p.bg,
        aspectmode: "cube",
        camera: { eye: { x: 1.5, y: 1.5, z: 1.2 } },
      },
      margin: { l: 10, r: 10, t: 30, b: 10 },
    },
  };
}

function buildScatter(data: number[], p: ChartPalette): PlotlyFigure {
  return {
    data: [
      {
        type: "scatter",
        y: data,
        mode: "markers",
        marker: { color: p.bar, size: 6, opacity: 0.7 },
      },
    ],
    layout: makeLinearLayout(p, "Index", "Value"),
  };
}

// Correlation Matrix (requires numpy corrcoef)

export type CorrelationComputeResult = {
  matrix: number[][];
  labels: string[];
};

function buildCorrelation(computed: CorrelationComputeResult, p: ChartPalette): PlotlyFigure {
  return {
    data: [
      {
        type: "heatmap",
        z: computed.matrix,
        x: computed.labels,
        y: computed.labels,
        colorscale: "RdBu",
        zmin: -1,
        zmax: 1,
        text: computed.matrix.map((row) => row.map((v) => v.toFixed(2))),
        texttemplate: "%{text}",
        textfont: { size: 10 },
      },
    ],
    layout: {
      ...makeLayout(p),
      margin: { l: 50, r: 30, t: 30, b: 50 },
    },
  };
}

function buildScatter3D(data2d: number[][], p: ChartPalette): PlotlyFigure {
  const x = data2d.map((row) => row[0]);
  const y = data2d.map((row) => row[1]);
  const z = data2d.map((row) => row[2] ?? 0);

  return {
    data: [
      {
        type: "scatter3d",
        x,
        y,
        z,
        mode: "markers",
        marker: { color: p.bar, size: 3, opacity: 0.7 },
      },
    ],
    layout: {
      ...makeLayout(p),
      scene: {
        xaxis: { title: "Col 1", ...makeAxis(p) },
        yaxis: { title: "Col 2", ...makeAxis(p) },
        zaxis: { title: "Col 3", ...makeAxis(p) },
        bgcolor: p.bg,
        camera: { eye: { x: 1.5, y: 1.5, z: 1.2 } },
      },
      margin: { l: 10, r: 10, t: 30, b: 10 },
    },
  };
}

function buildContour(data2d: number[][], p: ChartPalette): PlotlyFigure {
  return {
    data: [
      {
        type: "contour",
        z: data2d,
        colorscale: "Viridis",
        contours: { coloring: "heatmap" },
      },
    ],
    layout: makeLayout(p, "Col", "Row"),
  };
}

type BuildPlotlyFigureInput =
  | { chartType: "histogram"; computed: HistogramComputeResult }
  | { chartType: "fft"; computed: FFTComputeResult }
  | { chartType: "polyFit"; computed: PolyFitComputeResult }
  | { chartType: "boxplot"; data: number[] }
  | { chartType: "scatter"; data: number[] }
  | { chartType: "line"; data: number[] }
  | { chartType: "bar"; data: number[] }
  | { chartType: "heatmap"; data2d: number[][] }
  | { chartType: "surface"; data2d: number[][] }
  | { chartType: "scatter3d"; data2d: number[][] }
  | { chartType: "correlation"; computed: CorrelationComputeResult }
  | { chartType: "contour"; data2d: number[][] };

export function buildPlotlyFigure(
  input: BuildPlotlyFigureInput,
  theme: ChartTheme,
): { plotlyData: Record<string, unknown>[]; plotlyLayout: Record<string, unknown> } {
  const p = getPalette(theme);

  let figure: PlotlyFigure;

  switch (input.chartType) {
    case "histogram":
      figure = buildHistogram(input.computed, p);
      break;
    case "fft":
      figure = buildFFT(input.computed, p);
      break;
    case "polyFit":
      figure = buildPolyFit(input.computed, p);
      break;
    case "boxplot":
      figure = buildBoxplot(input.data, p);
      break;
    case "scatter":
      figure = buildScatter(input.data, p);
      break;
    case "line":
      figure = buildLine(input.data, p);
      break;
    case "bar":
      figure = buildBar(input.data, p);
      break;
    case "heatmap":
      figure = buildHeatmap(input.data2d, p);
      break;
    case "surface":
      figure = buildSurface(input.data2d, p);
      break;
    case "scatter3d":
      figure = buildScatter3D(input.data2d, p);
      break;
    case "correlation":
      figure = buildCorrelation(input.computed, p);
      break;
    case "contour":
      figure = buildContour(input.data2d, p);
      break;
  }

  return {
    plotlyData: figure.data,
    plotlyLayout: figure.layout,
  };
}
